/**
 * Money formatting — single source for euro amounts across Admin + Tenant.
 *
 * Always renders es-ES style: `1.234,56 €` (dot thousands, comma decimals,
 * NBSP before the symbol). Replaces ad-hoc ``${v.toFixed(2)}€`` which produced
 * `1234.56€` and looked like a bug next to the Intl-formatted figures.
 *
 * For NON-EUR amounts (e.g. Etsy listings priced in their own currency) use
 * ``formatMoney(value, code)`` and do NOT force EUR.
 */

import { CURRENCY_LIST, currencyInfo, hasAmbiguousSymbol, type CurrencyInfo } from './currencies';

const _eur = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Format a number as euros, es-ES style: `1.234,56 €`. Nullish/NaN → `0,00 €`. */
export function formatEuro(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return _eur.format(n);
}

const _amount = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format a number es-ES style WITHOUT the € symbol: `1.234,56`. Use where the
 * `€` is rendered separately (e.g. an input adornment on the left).
 */
export function formatAmount(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return _amount.format(n);
}

/**
 * Format an amount in an arbitrary ISO currency (es-ES locale). Falls back to
 * appending the raw code when the currency is unknown/invalid.
 */
export function formatMoney(value: number | null | undefined, currency = 'EUR'): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

// ── Per-record currency (Tenant localization) ────────────────────────────────
// Every money record carries its own ISO currency (see `currencies.ts` for the
// list). We render the EXACT symbol the user expects: `Intl` with
// style:'currency' produces `US$`/`CHF` (not `$`/`SFr`), so we format the
// number ourselves and append the symbol from the list — keeping the legacy
// `1.234,56 €` layout for every currency.

export type CurrencyCode = string;

export type CurrencyDef = CurrencyInfo;

/** The currencies a workshop or a record can be in, by code. */
export const CURRENCIES: Readonly<Record<string, CurrencyDef>> = Object.fromEntries(
  CURRENCY_LIST.map((c) => [c.code, c]),
);

/**
 * The symbol for an ISO code (case-insensitive). Empty → the euro's (a record
 * with no currency predates currencies and is in euros). A code the list does
 * not know → the code itself, never someone else's symbol: an unknown currency
 * must not be shown as euros.
 */
export function currencySymbol(code: string | null | undefined): string {
  const c = (code || '').trim().toUpperCase();
  if (!c) return '€';
  return currencyInfo(c)?.symbol ?? c;
}

/**
 * What to print after an amount in `code`: its symbol, or its ISO code when the
 * symbol is shared with another currency (`kr`, `L`) and the amount is not in
 * `workshopCurrency` — «100,00 kr» is clear in a Swedish workshop's own
 * money, but a Norwegian order there has to say «100,00 NOK».
 */
export function displaySymbol(code: string | null | undefined, workshopCurrency?: string | null): string {
  const c = (code || '').trim().toUpperCase();
  if (c && workshopCurrency && c !== workshopCurrency.trim().toUpperCase() && hasAmbiguousSymbol(c)) return c;
  return currencySymbol(code);
}

/**
 * Format `value` as the workshop's country writes an amount in that currency —
 * the same string its legal invoices print (owner's call, 2026-10-24: the
 * invoicing provider formats with these CLDR rules, measured in its sandbox).
 * The symbol goes where the country puts it, with its space, and it is the
 * symbol the country uses: `1234,56 €` (es-ES), `€ 1.234,56` (de-AT),
 * `EUR 1'234.56` (de-CH), `1234,56 US$` (dollars in Spain), `£5.00`. The
 * currency's own decimals; `decimals` overrides them (a chart axis prints
 * whole units); `symbol` replaces what Intl would print (the PDF uses it for
 * a code its fonts can draw). `workshopCurrency` is accepted for the callers
 * that still pass it and changes nothing: Intl already writes a foreign
 * currency's code where its symbol would be ambiguous. Nullish/NaN → 0.
 * The backend writes the same string (`format_locales.format_money`).
 */
export function formatWithSymbol(
  value: number | null | undefined,
  opts: { currency?: string; locale?: string; workshopCurrency?: string | null; symbol?: string; decimals?: number } = {},
): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const currency = (opts.currency || 'EUR').trim().toUpperCase();
  const decimals = opts.decimals ?? currencyInfo(currency)?.decimals ?? 2;
  let fmt: Intl.NumberFormat;
  try {
    fmt = new Intl.NumberFormat(opts.locale || 'es-ES', {
      style: 'currency', currency, minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    });
  } catch {
    // Not a currency Intl accepts: the number and whatever we can name it.
    const num = new Intl.NumberFormat(opts.locale || 'es-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n);
    return `${num}\u00a0${opts.symbol ?? currency}`;
  }
  if (opts.symbol === undefined) return fmt.format(n);
  return fmt.formatToParts(n).map((p) => (p.type === 'currency' ? opts.symbol : p.value)).join('');
}

